import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import type { Firestore } from 'firebase/firestore';

// Modern init (v10+)
import {
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
} from 'firebase/firestore';

// Fallbacks for v9
import {
  getFirestore,
  enableIndexedDbPersistence,
  enableMultiTabIndexedDbPersistence, // UPDATED 2025-08-19: remove ts-ignore-in-import, keep normal import
  setLogLevel,                         // UPDATED 2025-08-19: remove ts-ignore-in-import, keep normal import
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

export const app = initializeApp(firebaseConfig);

// Platform detection
const isIOS = Capacitor.getPlatform() === 'ios';

// Initialize Auth with iOS-specific settings
export const auth = isIOS 
  ? initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
    })
  : getAuth(app);

let db: Firestore;

try {
  // UPDATED 2025-08-19: v10-style init with persistent cache + platform-specific transport hints
  const firestoreSettings = {
    localCache: persistentLocalCache({
      // Native Capacitor apps have exactly one WebView — single-tab manager is
      // more appropriate and avoids unnecessary cross-tab coordination overhead.
      tabManager: persistentSingleTabManager({}),
    }),
    // Platform-specific settings
    experimentalAutoDetectLongPolling: true, // UPDATED 2025-08-19: prefer auto-detect over private API
    useFetchStreams: isIOS ? false : false,  // UPDATED 2025-08-19: disable fetch streams for stability on all platforms
  };

  db = initializeFirestore(app, firestoreSettings);
} catch {
  // UPDATED 2025-08-19: v9 fallback
  db = getFirestore(app);

  // Prefer multi-tab; fallback to single-tab — no top-level await
  (async () => {
    try {
      if (typeof enableMultiTabIndexedDbPersistence === 'function') {
        await enableMultiTabIndexedDbPersistence(db);
      } else {
        await enableIndexedDbPersistence(db);
      }
      // console.debug('[firebase] IndexedDB persistence enabled');
    } catch {
      // Not fatal; online-only will still work
      // console.warn('[firebase] Persistence not available; continuing online-only', err);
    }
  })();
}

// UPDATED 2025-08-19: Apply log level for both code paths
try {
  setLogLevel?.('warn');
} catch {
  // noop
}

export const functions = getFunctions(app);

// UPDATED 2025-08-19: top-level export (not inside catch)
export { db };